import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
import { Characteristic, Library } from '@library';
import { CharacteristicImage, CustomProtocolService, Evaluation, SampleType, Status } from '@core/data';
import { Dialog, Snackbar } from '@core/material';
import { coerceMomentProperty, coerceTimestampProperty, fileExtension } from '@core/utils';
import { LIBRARY } from '@app/evaluation/library';
import { CharacteristicImageGalleryState, InitCharacteristicImageGallery, makeCharacteristicImageDateGroup, PendingCharacteristicImageUpload, SaveCharacteristicImageGallery, ToggleCharacteristicImageDelete, UploadCharacteristicImage } from './characteristic-image-gallery.state';
import { CharacteristicImageViewerDialog, CharacteristicImageViewerDialogData } from './characteristic-image-viewer.dialog';

export interface CharacteristicImageGalleryDialogData {
    evalKey: string;
    protocolId: string;

    // Retained even though the Move feature (move-3-2) that needed these has been
    // removed (Epic 6) - evaluations/sampleKey/orgKey/sampleType are still passed
    // by every call site and harmless to keep on the data contract.
    sampleKey: string;
    orgKey: string;
    sampleType: SampleType;
    evaluations: Evaluation[];
}

export interface CharacteristicImageGroup {
    char: Characteristic;
    images: CharacteristicImage[];
}

@Component({
    selector: 'pv-characteristic-image-gallery-dialog',
    templateUrl: 'characteristic-image-gallery.dialog.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    host: {
        class: 'pv-characteristic-image-gallery-dialog pv-fullscreen-dialog'
    }
})
export class CharacteristicImageGalleryDialog implements OnInit, OnDestroy {

    @Select(CharacteristicImageGalleryState.images)
    images$: Observable<CharacteristicImage[]>;

    @Select(CharacteristicImageGalleryState.status)
    status$: Observable<Status>;

    @Select(CharacteristicImageGalleryState.saveStatus)
    saveStatus$: Observable<Status>;

    @Select(CharacteristicImageGalleryState.uploads)
    uploads$: Observable<PendingCharacteristicImageUpload[]>;

    @Select(CharacteristicImageGalleryState.pendingDeleteKeys)
    pendingDeleteKeys$: Observable<string[]>;

    groups: CharacteristicImageGroup[] = [];
    pendingDeleteKeys: string[] = [];
    hasActiveUpload = false;

    // Additive/persistent per-image date controls, so an in-progress edit survives unrelated store emissions.
    dateForms = new Map<string, FormGroup>();

    imageCapableChars: Characteristic[] = [];
    charControl = new FormControl(null);
    charFilterCtrl = new FormControl();
    filteredChars: ReplaySubject<Characteristic[]> = new ReplaySubject<Characteristic[]>(1);

    isDragging = false;
    maxSize = 40000000; // 40MB
    allowedExts = ['jpg', 'jpeg', 'png'];
    validationErrors: string[] = [];

    Status = Status;

    private _destroy$ = new Subject();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: CharacteristicImageGalleryDialogData,
        @Inject(LIBRARY) private _library: Library,
        private _custom_protocolService: CustomProtocolService,
        private _store: Store,
        private _dialog: Dialog,
        private _dialogRef: MatDialogRef<CharacteristicImageGalleryDialog>,
        private _snackbar: Snackbar,
        private _changeDetectorRef: ChangeDetectorRef,
    ){}

    ngOnInit(){

        this._store.dispatch(new InitCharacteristicImageGallery(this.data.evalKey, this.data.protocolId));

        this.images$.pipe(takeUntil(this._destroy$))
            .subscribe(images => {
                this.groups = this.buildGroups(images);
                this.syncDateForms(images);
                this._changeDetectorRef.markForCheck();
            });

        this.pendingDeleteKeys$.pipe(takeUntil(this._destroy$))
            .subscribe(keys => {
                this.pendingDeleteKeys = keys;
                this._changeDetectorRef.markForCheck();
            });

        this.uploads$.pipe(takeUntil(this._destroy$))
            .subscribe(uploads => {
                this.hasActiveUpload = uploads.some(upload => upload.status === Status.LOADING);
                this._changeDetectorRef.markForCheck();
            });

        // saveStatus only reflects Save/delete outcomes — kept separate from the
        // gallery's own load `status` so a failed save shows an inline error
        // instead of replacing the whole gallery with the generic load-error view
        this.saveStatus$.pipe(takeUntil(this._destroy$))
            .subscribe(saveStatus => {

                if(saveStatus === Status.COMPLETE){
                    this.dateForms.forEach(group => group.markAsPristine());
                    this._snackbar.info("Characteristic images updated");
                    this._dialogRef.close();
                }else if(saveStatus !== Status.OK && saveStatus !== Status.LOADING){
                    this._snackbar.error("Some changes could not be saved. Please try again.");
                }

                this._changeDetectorRef.markForCheck();

            });

        this.imageCapableChars = this.buildImageCapableChars();
        this.filteredChars.next(this.imageCapableChars);

        this.charFilterCtrl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this.filterChars());

    }

    ngOnDestroy(){
        this._destroy$.next();
        this._destroy$.complete();
    }

    trackGroup(index, group: CharacteristicImageGroup){
        return group.char.id;
    }

    trackImage(index, image: CharacteristicImage){
        return image.key;
    }

    trackChar(index, char: Characteristic){
        return char.id;
    }

    trackUpload(index, item: PendingCharacteristicImageUpload){
        return item.id;
    }

    openViewer(group: CharacteristicImageGroup, index: number){

        let data: CharacteristicImageViewerDialogData = {
            images: group.images,
            currentIndex: index,
            groupLabel: group.char.label,
        };

        this._dialog.openFullscreen(CharacteristicImageViewerDialog, {data, disableClose: false}, false);
    }

    get selectedChar(): Characteristic {
        return this.imageCapableChars.find(char => char.id === this.charControl.value) || null;
    }

    isSaveError(saveStatus: Status): boolean {
        return saveStatus !== null && saveStatus !== Status.OK && saveStatus !== Status.LOADING && saveStatus !== Status.COMPLETE;
    }

    toggleDelete(event: Event, image: CharacteristicImage){
        event.stopPropagation();
        this._store.dispatch(new ToggleCharacteristicImageDelete(image.key));
    }

    dateGroupFor(image: CharacteristicImage): FormGroup {
        return this.dateForms.get(image.key);
    }

    save(){
        // defensive: mirrors the Save button's [disabled] binding, in case this
        // is ever called from somewhere other than that button
        if(this.hasActiveUpload) return;

        // A date edit on an image also pending delete is moot - the delete wins, so drop it from the batch.
        const dateUpdates = Array.from(this.dateForms.values())
            .filter(group => group.dirty && !this.pendingDeleteKeys.includes(group.value.key))
            .map(group => ({ key: group.value.key, takenAt: coerceTimestampProperty(group.value.takenAt) }));

        this._store.dispatch(new SaveCharacteristicImageGallery(dateUpdates));
    }

    cancel(){
        this._dialogRef.close();
    }

    fileInputChanged(event: Event){

        let input = <HTMLInputElement> event.target;

        if(input.files && input.files.length > 0){
            this.uploadFiles(input.files);
        }

    }

    onDragOver(event: DragEvent){

        if(!this.selectedChar) return;

        if(event.dataTransfer){
            event.dataTransfer.dropEffect = "copy";
        }

        if(!this.isDragging){
            this.isDragging = true;
        }
        event.preventDefault();
        event.stopPropagation();
    }

    onDragLeave(event: DragEvent){

        if(this.isDragging){
            this.isDragging = false;
        }

        event.preventDefault();
    }

    onDrop(event: DragEvent){

        if(!this.selectedChar) return;

        if(event.dataTransfer){
            event.dataTransfer.dropEffect = 'copy';

            this.uploadFiles(event.dataTransfer.files);
        }

        this.isDragging = false;

        event.preventDefault();
        event.stopPropagation();
    }

    private uploadFiles(list: FileList){

        if(!this.selectedChar) return;

        if(this.validateFiles(list)){
            for(let i = 0; i < list.length; i++){
                this._store.dispatch(new UploadCharacteristicImage(list.item(i), this.selectedChar.id));
            }
        }

    }

    private validateFiles(files: FileList){

        let valid = true;
        this.validationErrors = [];

        for(let i = 0; i < files.length; i++){
            let file = files[i];
            let fileExt = fileExtension(file.name).toLowerCase();

            if(!this.allowedExts.includes(fileExt)){
                this.validationErrors.push(`${file.name} is not a valid file type.`);
            }

            if(file.size > this.maxSize){
                this.validationErrors.push(`${file.name} exceeds the maximum file size.`);
            }

        }

        if(this.validationErrors.length > 0){
            valid = false;
        }

        return valid;
    }

    private buildImageCapableChars(): Characteristic[] {

        if(!this.data.protocolId) return [];

        const protocol = this._custom_protocolService.allProtocols().get(this.data.protocolId);

        if(!protocol) return [];

        return protocol.chars
            .map(charId => this._library.chars.get(charId))
            .filter(char => !!char && char.imageAnalysis === true);
    }

    private filterChars(){

        if(!this.imageCapableChars) return;

        let search = this.charFilterCtrl.value;

        if(!search){
            this.filteredChars.next(this.imageCapableChars.slice());
            return;
        }

        search = search.toLowerCase();

        this.filteredChars.next(
            this.imageCapableChars.filter(char => char.label.toLowerCase().indexOf(search) > -1)
        );

    }

    private buildGroups(images: CharacteristicImage[]): CharacteristicImageGroup[] {

        if(!Array.isArray(images) || !this.data.protocolId) return [];

        const protocol = this._custom_protocolService.allProtocols().get(this.data.protocolId);

        if(!protocol) return [];

        const charIdsWithImages = new Set(images.map(image => image.charId));

        return protocol.chars
            .filter(charId => charIdsWithImages.has(charId))
            .map(charId => this._library.chars.get(charId))
            .filter(char => !!char)
            .map(char => {

                const groupImages = images
                    .filter(image => image.charId === char.id)
                    .concat()
                    .sort((a, b) => {
                        const aCreatedAt = a.createdAt || '';
                        const bCreatedAt = b.createdAt || '';
                        if (aCreatedAt < bCreatedAt) return -1;
                        if (aCreatedAt > bCreatedAt) return 1;
                        return 0;
                    });

                return { char, images: groupImages };
            });
    }

    private syncDateForms(images: CharacteristicImage[]){

        const keys = new Set(images.map(image => image.key));

        Array.from(this.dateForms.keys())
            .filter(key => !keys.has(key))
            .forEach(key => this.dateForms.delete(key));

        images.forEach(image => {

            const existing = this.dateForms.get(image.key);

            if(!existing){
                this.dateForms.set(image.key, makeCharacteristicImageDateGroup(image));
                return;
            }

            // Pick up a save made elsewhere (e.g. the viewer dialog) - but never clobber an unsaved local edit.
            if(!existing.dirty){
                existing.get('takenAt').setValue(coerceMomentProperty(image.takenAt));
            }

        });

    }

}
